import React from "react";
import {
	Box,
	Typography,
	Breadcrumbs,
	Link,
	Paper,
	Button,
	Divider,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
	Edit as EditIcon,
	AccountTree as AccountTreeIcon,
	Language as LanguageIcon,
	Business as BusinessIcon,
} from "@mui/icons-material";
import { zoullooColors } from "../../theme";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useCompanyGroup } from "../../hooks/useCompanyGroup";

const CompanyGroupDetails: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const groupId = parseInt(id || "0");

	const { t } = useTranslation();
	const navigate = useNavigate();

	const { data: companyGroup, isLoading, error } = useCompanyGroup(groupId);

	const handleEdit = () => {
		navigate(`/company-groups/${groupId}/edit`);
	};

	const handleNavigateToList = () => {
		navigate("/company-groups");
	};

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" height="400px">
				<CircularProgress />
			</Box>
		);
	}

	if (error || !companyGroup) {
		return (
			<Box p={3}>
				<Typography color="error">
					{t("companyGroup.errorLoading")}:{" "}
					{error?.message || t("companyGroup.notFound")}
				</Typography>
			</Box>
		);
	}

	return (
		<Box p={3}>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
				<Typography variant="h2">{companyGroup.name}</Typography>
				<Button
					variant="contained"
					startIcon={<EditIcon />}
					onClick={handleEdit}
					sx={{
						backgroundColor: zoullooColors.secondary.main,
						"&:hover": {
							backgroundColor: zoullooColors.secondary.dark,
						},
					}}
				>
					{t("common.edit")}
				</Button>
			</Box>

			<Breadcrumbs separator=">" aria-label="breadcrumb" sx={{ mb: 3 }}>
				<Link
					underline="hover"
					color="inherit"
					onClick={handleNavigateToList}
					sx={{ cursor: "pointer" }}
				>
					{t("companyGroup.list")}
				</Link>
				<Typography color="text.primary">{companyGroup.name}</Typography>
			</Breadcrumbs>

			{/* Group Information - full width banner */}
			<Paper elevation={3} sx={{ p: 3, mb: 3 }}>
				<Box display="flex" alignItems="center" mb={2}>
					<AccountTreeIcon sx={{ mr: 1, color: zoullooColors.secondary.dark }} />
					<Typography variant="h4" color={zoullooColors.secondary.dark}>
						{t("companyGroup.groupInformation")}
					</Typography>
				</Box>
				<Divider sx={{ mb: 2 }} />

				<Grid container spacing={3}>
					<Grid size={{ xs: 12, sm: 4 }}>
						<Typography variant="subtitle2" color="text.secondary">
							{t("companyGroup.name")}
						</Typography>
						<Typography variant="body1">
							{companyGroup.name}
						</Typography>
					</Grid>

					<Grid size={{ xs: 12, sm: 4 }}>
						<Typography variant="subtitle2" color="text.secondary">
							{t("companyGroup.website")}
						</Typography>
						{companyGroup.website ? (
							<Box display="flex" alignItems="center">
								<LanguageIcon
									sx={{
										mr: 1,
										fontSize: "small",
										color: zoullooColors.primary.main,
									}}
								/>
								<Link
									href={companyGroup.website}
									target="_blank"
									rel="noopener"
								>
									{companyGroup.website}
								</Link>
							</Box>
						) : (
							<Typography variant="body1" color="text.secondary">-</Typography>
						)}
					</Grid>

					<Grid size={{ xs: 12, sm: 4 }}>
						<Typography variant="subtitle2" color="text.secondary">
							{t("companyGroup.companiesCount")}
						</Typography>
						<Chip
							label={companyGroup.companies?.length || 0}
							size="small"
							sx={{
								mt: 0.5,
								backgroundColor: zoullooColors.secondary.alpha,
								color: zoullooColors.secondary.dark,
								fontWeight: 600,
							}}
						/>
					</Grid>
				</Grid>
			</Paper>

			{/* Companies table - full width */}
			<Paper elevation={3} sx={{ p: 3 }}>
				<Box display="flex" alignItems="center" mb={2}>
					<BusinessIcon sx={{ mr: 1, color: zoullooColors.secondary.dark }} />
					<Typography variant="h4" color={zoullooColors.secondary.dark}>
						{t("companyGroup.companies")}
					</Typography>
				</Box>
				<Divider sx={{ mb: 2 }} />

				{companyGroup.companies && companyGroup.companies.length > 0 ? (
					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>{t("company.legalName")}</TableCell>
									<TableCell>{t("company.tradingName")}</TableCell>
									<TableCell>{t("company.industry")}</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{companyGroup.companies.map((company) => (
									<TableRow
										key={company.id}
										hover
										sx={{ cursor: "pointer" }}
										onClick={() => navigate(`/companies/${company.id}`)}
									>
										<TableCell>
											<Link
												underline="hover"
												sx={{
													fontWeight: 500,
													color: zoullooColors.secondary.dark,
													cursor: "pointer",
												}}
											>
												{company.legalName}
											</Link>
										</TableCell>
										<TableCell>
											{company.tradingName || "-"}
										</TableCell>
										<TableCell>
											{company.sector?.label || "-"}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				) : (
					<Typography variant="body1" color="text.secondary">
						{t("companyGroup.noCompaniesInGroup")}
					</Typography>
				)}
			</Paper>
		</Box>
	);
};

export default CompanyGroupDetails;
